/*
 * pogi-kit-builder — homepage bundle bottom-sheet (§13.5 / §13.5a).
 * Opens when any [data-open-kit-builder] CTA (hero, sticky, etc.) is clicked
 * — document-level click delegation. Toggle products + Subscribe & save,
 * see a live subtotal / discounted total / free-shipping gap, and add every
 * selected variant to the cart in one POST /cart/add.js.
 *
 * Discount %, free-ship threshold, and money format come from data-* on the
 * host element (one source, set in the section schema — not hardcoded here).
 * Each product row carries its own data-variant-id / data-selling-plan-id /
 * data-price (cents). Rows with no real variant id are skipped on add.
 */
import { CartUpdateEvent } from '@theme/events';

/* Market-currency guard for JS-DERIVED secondary prices. The row data-price values are
   the shop BASE-currency cents (USD) and money() formats with the shop base money_format
   ('${{amount}}') — so on a non-USD market the subtotal/total would show a wrong '$'
   figure. The real prices localize via Liquid `| money` (the per-row blurb). To never
   show a wrong derived total off-USD we HIDE the live subtotal/total/strike when the
   active market currency isn't USD (the shop base). On USD this is true → identical
   behavior. */
const SHOW_DERIVED = !(window.Shopify && Shopify.currency && Shopify.currency.active) || Shopify.currency.active === 'USD';

/* Off-USD the running total can't be presentment-converted exactly client-side, so we
   ESTIMATE it: base cents × Shopify's active conversion rate, formatted with Intl
   (correct symbol + decimals per currency). It's a preview; checkout is exact. Prefixed
   (約 on JA) for honesty. Returns null if rate/Intl unavailable → caller blanks it. */
const _CUR = (window.Shopify && Shopify.currency) || {};
const _RATE = parseFloat(_CUR.rate) || 1;
const _ACTIVE = _CUR.active || 'USD';
const presentmentEst = (cents) => {
  const v = (cents / 100) * _RATE;
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: _ACTIVE, currencyDisplay: 'narrowSymbol' }).format(v); }
  catch (e) { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: _ACTIVE }).format(v); } catch (e2) { return null; } }
};

/* i18n: JA overrides come from the [data-kit-i18n] blob the section renders via
   `| t`. EVERY read falls back to the exact English literal that used to be
   hardcoded, so a missing key can never change the EN site. [[count]] placeholder
   (not {{ }}) so Liquid's t-filter never eats it. */
const kitTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

class PogiKitBuilder extends HTMLElement {
  connectedCallback() {
    this.discount = (parseFloat(this.dataset.discount) || 0) / 100;
    this.freeShip = parseInt(this.dataset.freeship, 10) || 0; // cents
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.approx = this.dataset.approx || ''; // off-USD estimate prefix (約 on JA, '' on EN)
    const i18nEl = this.querySelector('[data-kit-i18n]');
    this.I = {};
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
    this.t = (k, fb, vars) => kitTpl(this.I, k, fb, vars);
    this.sheet = this.querySelector('[data-kit-sheet]');
    this.products = Array.from(this.querySelectorAll('[data-kit-product]'));
    this.subToggle = this.querySelector('[data-kit-subscribe]');

    this.addEventListener('click', (e) => {
      const prod = e.target.closest('[data-kit-product]');
      if (prod) { this.toggle(prod); return; }
      if (e.target.closest('[data-kit-subscribe]')) { this.toggleSub(); return; }
      if (e.target.closest('[data-kit-add]')) { this.add(); return; }
      if (e.target.closest('[data-kit-close]')) { this.close(); return; }
      if (e.target === this) this.close(); // overlay click
    });

    // Global open trigger: any [data-open-kit-builder] CTA, anywhere on the page.
    this._onDocClick = (e) => {
      if (e.target.closest('[data-open-kit-builder]')) {
        e.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this._onDocClick);
    this._onKey = (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); };
    document.addEventListener('keydown', this._onKey);

    this.recompute();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  // Off-USD estimate with the locale prefix (約); '' if unavailable so the slot blanks.
  approxFmt(cents) { const e = presentmentEst(cents); return e ? (this.approx + e) : ''; }

  money(cents) {
    const amt = cents / 100;
    const grp = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grp(Math.round(amt).toString()))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, grp(Math.round(amt).toString()));
  }

  open() {
    this._prevOverflow = document.body.style.overflow;
    this._prevPaddingRight = document.body.style.paddingRight;
    // POGI: compensate the scrollbar width so locking scroll doesn't shift the page right.
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    document.body.style.overflow = 'hidden';
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    document.body.style.overflow = this._prevOverflow || '';
    document.body.style.paddingRight = this._prevPaddingRight || '';
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
  }

  toggle(el) {
    const on = el.classList.toggle('is-selected');
    el.setAttribute('aria-pressed', String(on));
    this.recompute();
  }

  toggleSub() {
    const on = this.subToggle.classList.toggle('is-on');
    this.subToggle.setAttribute('aria-pressed', String(on));
    this.recompute();
  }

  selected() {
    return this.products.filter((p) => p.classList.contains('is-selected'));
  }

  subscribeOn() {
    return this.subToggle && this.subToggle.classList.contains('is-on');
  }

  recompute() {
    const sel = this.selected();
    const subtotal = sel.reduce((sum, p) => sum + (parseInt(p.dataset.price, 10) || 0), 0);
    const sub = this.subscribeOn();
    const total = sub ? Math.round(subtotal * (1 - this.discount)) : subtotal;

    const set = (sel2, val) => { const el = this.querySelector(sel2); if (el) el.textContent = val; };
    set('[data-kit-count]', this.t('count', sel.length + (sel.length === 1 ? ' item' : ' items'), { count: sel.length }));
    // Off-USD: show a rate-based estimate (約…) instead of blanking the total.
    set('[data-kit-subtotal]', SHOW_DERIVED ? this.money(subtotal) : this.approxFmt(subtotal));
    set('[data-kit-total]', SHOW_DERIVED ? this.money(total) : this.approxFmt(total));

    const strike = this.querySelector('[data-kit-strike]');
    if (strike) {
      const showStrike = sub && subtotal > 0;
      const strikeStr = SHOW_DERIVED ? this.money(subtotal) : this.approxFmt(subtotal);
      strike.textContent = showStrike ? strikeStr : '';
      strike.style.display = (showStrike && strikeStr) ? '' : 'none';
    }

    // Free shipping is universal now — every kit ships free, no threshold.
    const ship = this.querySelector('[data-kit-ship]');
    if (ship) {
      ship.textContent = this.t('free_ship', '✓ Free shipping on every order');
      ship.dataset.state = 'ok';
    }

    const add = this.querySelector('[data-kit-add]');
    if (add) {
      if (sel.length === 0) {
        add.disabled = true;
        add.textContent = this.t('pick_one', 'Pick at least one product');
      } else {
        add.disabled = false;
        add.textContent = this.t('add_count', 'Add ' + sel.length + (sel.length === 1 ? ' item' : ' items') + ' to cart', { count: sel.length });
      }
    }
  }

  async add() {
    const sub = this.subscribeOn();
    const items = this.selected().map((p) => {
      const item = { id: parseInt(p.dataset.variantId, 10), quantity: 1 };
      if (sub && p.dataset.sellingPlanId) item.selling_plan = parseInt(p.dataset.sellingPlanId, 10);
      return item;
    }).filter((i) => i.id);
    if (!items.length) return;

    const btn = this.querySelector('[data-kit-add]');
    const label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = this.t('adding', 'Adding…'); }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('add failed');
      let cart = null;
      try { cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json(); } catch (err) { /* non-fatal */ }
      // Class-based event so Horizon's header bubble + drawer actually refresh
      // (a plain CustomEvent('cart:update') is unheard — was the sync bug).
      document.dispatchEvent(new CartUpdateEvent(cart || {}, 'pogi-kit-builder', {
        source: 'pogi-kit-builder',
        itemCount: cart ? cart.item_count : undefined,
      }));
      this.close();
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
      else window.location.href = '/cart';
    } catch (e) {
      if (btn) { btn.disabled = false; btn.textContent = this.t('try_again', 'Try again'); }
      return;
    }
    if (btn) { btn.disabled = false; btn.textContent = label; }
  }
}

if (!customElements.get('pogi-kit-builder')) {
  customElements.define('pogi-kit-builder', PogiKitBuilder);
}
